import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  BookOpen, GraduationCap, ClipboardList, Globe, FileSpreadsheet,
  Shield, ArrowRight, Lock, Loader2, Users, CheckCircle2,
  Sparkles, Hammer, BarChart3, ShieldCheck, Calendar, UsersRound,
  AlertTriangle, TrendingUp, Clock, Euro, Layers, Download,
} from "lucide-react";
import { toast } from "sonner";

const MODULES = [
  {
    key: "formation_bibliotheque", title: "Bibliotheque de programmes",
    sub: "Programmes = hub central : modules, evaluations, docs Qualiopi",
    icon: BookOpen, to: "/admin/formation/bibliotheque", color: "#f59e0b", metric: "programs",
  },
  {
    key: "formation_bibliotheque", title: "Modules de cours",
    sub: "Compilez vos sequences SCORM en modules de cours ordonnes, publies et reutilisables",
    icon: Layers, to: "/admin/formation/modules", color: "#38bdf8", metric: "modules",
  },
  {
    key: "formation_lms", title: "Application & hors ligne",
    sub: "Installer Blitzz Formation sur tablettes et ordinateurs, mode hors ligne, mises a jour",
    icon: Download, to: "/admin/formation/app", color: "#22d3ee", metric: null,
  },
  {
    key: "formation_lms", title: "Sessions",
    sub: "Planifiez, gerez emargements, signatures et documents",
    icon: Calendar, to: "/admin/formation/sessions", color: "#3b82f6", metric: "sessions",
  },
  {
    key: "formation_lms", title: "Inscriptions",
    sub: "Pipeline devis -> signe -> acompte -> inscrit + profils clients",
    icon: UsersRound, to: "/admin/formation/inscriptions", color: "#10b981", metric: "pipeline",
  },
  {
    key: "formation_evaluations", title: "Evaluations",
    sub: "Pre / chaud / froid / managers - NPS, taux de satisfaction",
    icon: ClipboardList, to: "/admin/formation/evaluations", color: "#8b5cf6", metric: "eval_responses",
  },
  {
    key: "formation_evaluations", title: "Documents Qualiopi",
    sub: "Contrats, conventions, attestations avec balises dynamiques",
    icon: CheckCircle2, to: "/admin/formation/doc-templates", color: "#22d3ee", metric: "docs",
  },
  {
    key: "formation_lms", title: "Statistiques BPF",
    sub: "Bilan Pedagogique et Financier annuel - CA + heures + stagiaires",
    icon: BarChart3, to: "/admin/formation/bpf", color: "#14b8a6", metric: "ca_ht",
  },
  {
    key: "formation_lms", title: "Conformite Qualiopi",
    sub: "Audit automatise des indicateurs par session + actions",
    icon: ShieldCheck, to: "/admin/formation/sessions", color: "#ef4444", metric: "compliance",
  },
  {
    key: "formation_catalogue", title: "Catalogue public",
    sub: "Marketplace accessible sans connexion + leads entrants",
    icon: Globe, to: "/catalogue", adminTo: "/admin/catalogue-leads", color: "#f7c935", metric: "leads", external: true,
  },
  {
    key: "formation_edof", title: "Export EDOF",
    sub: "Export XML conforme Mon Compte Formation",
    icon: FileSpreadsheet, to: "/admin/formation/bibliotheque", toQuery: "?tab=export_edof", color: "#06b6d4",
  },
];

function formatNumber(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "number" && n >= 10000) return n.toLocaleString("fr-FR");
  return String(n);
}

export default function FormationBuilderPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    programs: 0, modules: 0, sessions: 0, sessions_active: 0, sessions_upcoming: 0,
    pipeline: 0, enrollments_total: 0, enrollments_confirmed: 0,
    eval_responses: 0, satisfaction: null, nps: null,
    docs: 0, ca_ht: 0, hours: 0, stagiaires_formes: 0,
    leads: 0, compliance_alerts: 0, avg_compliance_score: null,
  });
  const [activeModules, setActiveModules] = useState([]);

  const isBlitzzStaff = !user?.tenant_id;

  const isActive = (key) => {
    if (isBlitzzStaff) return true;
    if (activeModules.includes(key)) return true;
    if (key.startsWith("formation_") && activeModules.includes("formations")) return true;
    if (key === "qualiopi" && activeModules.includes("qualiopi")) return true;
    return false;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const calls = [
          api.get("/lms/programs").catch(() => ({ data: [] })),
          api.get("/lms/sessions").catch(() => ({ data: [] })),
          api.get("/lms/inscriptions/dashboard").catch(() => ({ data: null })),
          api.get("/lms/stats/bpf").catch(() => ({ data: null })),
          api.get("/lms/lessons").catch(() => ({ data: [] })),
          api.get("/lms/qualiopi/documents").catch(() => ({ data: [] })),
          api.get("/catalogue-leads").catch(() => ({ data: [] })),
        ];
        if (!isBlitzzStaff) {
          calls.push(api.get("/tenant/dashboard").catch(() => ({ data: { active_modules: [] } })));
        }
        const [p, s, insc, bpf, mods, docs, leads, td] = await Promise.all(calls);

        const sessions = s.data || [];
        const today = new Date().toISOString().slice(0, 10);
        const sess_upcoming = sessions.filter((x) => (x.start_date || "") > today).length;
        const sess_active = sessions.filter((x) => {
          const st = x.start_date || "";
          const en = x.end_date || st;
          return st <= today && en >= today;
        }).length;

        setMetrics({
          programs: (p.data || []).length,
          modules: (mods.data || []).length,
          sessions: sessions.length,
          sessions_active: sess_active,
          sessions_upcoming: sess_upcoming,
          pipeline: insc.data?.pipeline_count || 0,
          enrollments_total: insc.data?.total_enrollments || 0,
          enrollments_confirmed: insc.data?.confirmed_count || 0,
          eval_responses: bpf.data?.satisfaction?.responses || 0,
          satisfaction: bpf.data?.satisfaction?.avg_rating ?? null,
          nps: bpf.data?.satisfaction?.nps ?? null,
          docs: (docs.data || []).length,
          ca_ht: bpf.data?.total_ca_ht || 0,
          hours: bpf.data?.total_hours || 0,
          stagiaires_formes: bpf.data?.total_stagiaires || 0,
          leads: (leads.data || []).length,
          compliance_alerts: 0,
          avg_compliance_score: null,
        });
        if (td) setActiveModules(td.data?.active_modules || []);
      } catch (e) {
        toast.error("Erreur chargement: " + (e.response?.data?.detail || e.message));
      }
      setLoading(false);
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useMemo(() => ([
    { label: "Sessions actives", value: metrics.sessions_active, sub: `+${metrics.sessions_upcoming} a venir`, icon: Calendar, color: "#3b82f6" },
    { label: "Stagiaires confirmes", value: metrics.enrollments_confirmed, sub: metrics.pipeline ? `+${metrics.pipeline} en pipeline` : "Aucun dossier en attente", icon: Users, color: "#10b981" },
    { label: "Heures dispensees", value: formatNumber(Math.round(metrics.hours)), sub: `${metrics.stagiaires_formes} formes (annee)`, icon: Clock, color: "#f7c935" },
    { label: "CA HT realise", value: formatNumber(Math.round(metrics.ca_ht)), sub: "EUR HT (annee en cours)", icon: Euro, color: "#f59e0b" },
    { label: "Satisfaction", value: metrics.satisfaction ? `${metrics.satisfaction}/5` : "—", sub: `${metrics.eval_responses} reponses`, icon: TrendingUp, color: "#8b5cf6" },
    { label: "NPS", value: metrics.nps ?? "—", sub: "Net Promoter Score", icon: Sparkles, color: "#06b6d4" },
  ]), [metrics]);

  // Per-card metrics (displayed inside the card)
  const metricFor = (m) => {
    switch (m.metric) {
      case "programs": return { n: metrics.programs, u: "programmes" };
      case "modules": return { n: metrics.modules, u: "modules de cours" };
      case "sessions": return { n: metrics.sessions, u: "sessions", sub: `${metrics.sessions_active} en cours · ${metrics.sessions_upcoming} a venir` };
      case "pipeline": return { n: metrics.enrollments_total, u: "inscriptions", sub: `${metrics.enrollments_confirmed} confirmees · ${metrics.pipeline} en pipeline` };
      case "eval_responses": return { n: metrics.eval_responses, u: "reponses", sub: metrics.satisfaction ? `${metrics.satisfaction}/5 · NPS ${metrics.nps ?? "—"}` : "" };
      case "docs": return { n: metrics.docs, u: "documents" };
      case "ca_ht": return { n: formatNumber(Math.round(metrics.ca_ht)), u: "EUR HT" };
      case "compliance": return { n: metrics.sessions, u: "sessions auditables", sub: "Cliquer sur une session -> onglet Conformite" };
      case "leads": return { n: metrics.leads, u: "leads" };
      default: return null;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="formation-builder-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#f7c935] font-semibold mb-2">
            <Hammer className="w-3.5 h-3.5" /> Pilotage Formation
          </div>
          <h1 className="text-4xl font-black tracking-tighter leading-none">
            Centre de commande <span style={{ color: "#f7c935" }}>formation</span>
          </h1>
          <p className="text-slate-400 mt-3 max-w-2xl">
            Vue unifiee : bibliotheque, sessions, inscriptions, evaluations, conformite Qualiopi, stats BPF.
            Chaque widget ci-dessous ouvre le module correspondant.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 bg-[#0b0811] border border-white/10 rounded-full px-4 py-2">
          <Sparkles className="w-3.5 h-3.5 text-[#f7c935]" />
          {isBlitzzStaff ? "Mode Blitzz staff — tous les modules" : `${activeModules.filter(m => m === "formations" || m.startsWith("formation_") || m === "qualiopi").length} modules actifs`}
        </div>
      </div>

      {/* KPI strip — 6 high-level indicators */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {kpis.map((k) => (
          <div key={k.label} className="bg-[#14101c] border border-white/10 rounded-xl p-4" data-testid={`fb-kpi-${k.label.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{k.label}</span>
              <k.icon className="w-3.5 h-3.5" style={{ color: k.color, opacity: 0.8 }} />
            </div>
            <div className="text-2xl font-black text-white">{loading ? "—" : k.value}</div>
            {k.sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{k.sub}</div>}
          </div>
        ))}
      </div>

      {loading && <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935]" /></div>}

      {/* Module widgets with KPI preview */}
      {!loading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fb-modules-grid">
          {MODULES.map((m, i) => {
            const active = isActive(m.key);
            const Icon = m.icon;
            const met = metricFor(m);
            return (
              <button
                key={m.key + "-" + i}
                onClick={() => {
                  if (!active) { toast.info("Module non active sur votre organisation."); return; }
                  const url = m.to + (m.toQuery || "");
                  if (m.external) window.open(url, "_blank", "noopener,noreferrer");
                  else navigate(url);
                }}
                data-testid={`fb-card-${m.key}-${i}`}
                className={`group text-left relative overflow-hidden rounded-2xl p-5 border transition-all ${
                  active
                    ? "bg-[#14101c] border-white/10 hover:border-[#f7c935]/40 hover:-translate-y-0.5"
                    : "bg-[#0b0811] border-white/5 opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full blur-3xl opacity-20 pointer-events-none" style={{ background: m.color }} />
                <div className="flex items-start justify-between mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${m.color}20`, border: `1px solid ${m.color}40` }}>
                    <Icon className="w-6 h-6" style={{ color: m.color }} />
                  </div>
                  {active
                    ? <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 font-bold uppercase tracking-wider">Actif</span>
                    : <span className="flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full bg-white/5 text-slate-500 font-bold uppercase tracking-wider"><Lock className="w-2.5 h-2.5" /> Desactive</span>
                  }
                </div>
                <h3 className="font-bold text-white text-base mb-1">{m.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{m.sub}</p>

                {/* Inline metric widget */}
                {active && met && (
                  <div className="mt-4 pt-3 border-t border-white/5" data-testid={`fb-metric-${m.metric}-${i}`}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-black" style={{ color: m.color }}>{met.n}</span>
                      <span className="text-xs text-slate-400">{met.u}</span>
                    </div>
                    {met.sub && <div className="text-[10px] text-slate-500 mt-0.5">{met.sub}</div>}
                  </div>
                )}

                {active && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-slate-500 group-hover:text-[#f7c935] transition-colors">
                    <span className="font-semibold">Ouvrir</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                )}

                {active && m.key === "formation_catalogue" && m.adminTo && (
                  <Link to={m.adminTo} onClick={(e) => e.stopPropagation()} className="absolute bottom-4 right-12 text-[10px] font-semibold text-[#f7c935] hover:underline">
                    Voir les leads
                  </Link>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Pipeline alert — shown when pipeline > 0 */}
      {!loading && metrics.pipeline > 0 && (
        <div className="mt-8 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3" data-testid="fb-pipeline-alert">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-white text-sm">{metrics.pipeline} dossier{metrics.pipeline > 1 ? "s" : ""} a finaliser</div>
            <div className="text-xs text-slate-400">Des stagiaires en attente de signature de devis, acompte ou paiement complet.</div>
          </div>
          <button onClick={() => navigate("/admin/formation/inscriptions")} className="text-xs font-bold text-amber-400 hover:underline">Ouvrir le pilotage →</button>
        </div>
      )}

      {/* Quick help */}
      <div className="mt-8 bg-[#0b0811] border border-white/10 rounded-2xl p-5" data-testid="fb-help">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#f7c935] flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-bold text-white mb-1">Besoin d'un module supplementaire ?</div>
            <p className="text-slate-400 text-xs leading-relaxed">
              Les widgets grises ne sont pas inclus dans votre offre actuelle. Demandez leur activation
              a votre administrateur Blitzz, ou ajustez directement l'offre du tenant dans Master Admin &gt; Offres.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
